// Navigation.js (Modern Navigation with BottomNavBar for Mobile)
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  FaUserAlt,
  FaClipboardList,
  FaBoxOpen,
  FaSignOutAlt,
  FaTachometerAlt,
  FaHistory,
  FaEllipsisH,
} from "react-icons/fa";

// --- Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
};

// --- Sidebar for Desktop
const Sidebar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 220px;
  background-color: ${({ theme }) => theme.primaryColor};
  padding: ${({ theme }) => theme.padding};
  border-radius: 0 10px 10px 0;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margin};
  z-index: 1000;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 40px 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margin};
`;

const NavItem = styled.li``;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #fff;
  text-decoration: none;
  padding: 12px 20px;
  border-radius: ${({ theme }) => theme.borderRadius};
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #2980b9;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 12px 20px;
  font-size: 1rem;
  text-align: left;

  &:hover {
    background-color: #2980b9;
  }
`;

// --- Bottom Navbar for Mobile
const BottomBar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  height: 60px;
  background-color: #ffffff;
  display: flex;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1001;

  @media (min-width: 768px) {
    display: none;
  }
`;

const BottomNavLink = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;
  color: ${({ active }) => (active ? "#3498db" : "#777")};
  text-decoration: none;

  svg {
    font-size: 18px;
    margin-bottom: 2px;
  }
`;

const Navigation = ({ user, onLogout }) => {
  const isMobile = useIsMobile();
  const location = useLocation();

  if (!user) return null;

  const commonLinks = (
    <>
      <NavItem>
        <NavLink to="/profile">
          <FaUserAlt /> Profile
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink to="/orders">
          <FaBoxOpen /> Orders
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink to="/timeline">
          <FaHistory /> Timeline
        </NavLink>
      </NavItem>
    </>
  );

  const renderInventoryLink = () => {
    if (user.role === "tech" || user.role === "warehouseAdmin") {
      return (
        <NavItem>
          <NavLink to="/my-inventory">
            <FaClipboardList /> Inventory
          </NavLink>
        </NavItem>
      );
    }
    return null;
  };

  return (
    <>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar>
          <NavList>
            {commonLinks}
            {renderInventoryLink()}
            {user.role !== "tech" && (
              <NavItem>
                <NavLink to="/dashboard">
                  <FaTachometerAlt /> Dashboard
                </NavLink>
              </NavItem>
            )}
            <NavItem>
              <LogoutButton onClick={onLogout}>
                <FaSignOutAlt /> Logout
              </LogoutButton>
            </NavItem>
          </NavList>
        </Sidebar>
      )}

      {/* Mobile Bottom Bar */}
      {isMobile && (
        <BottomBar>
          <BottomNavLink to="/profile" active={location.pathname === "/profile"}>
            <FaUserAlt /> Profile
          </BottomNavLink>

          {(user.role === "tech" || user.role === "warehouseAdmin") && (
            <BottomNavLink to="/my-inventory" active={location.pathname === "/my-inventory"}>
              <FaClipboardList /> Inventory
            </BottomNavLink>
          )}

          <BottomNavLink to="/orders" active={location.pathname.startsWith("/orders")}> 
            <FaBoxOpen /> Orders
          </BottomNavLink>

          <BottomNavLink to="/timeline" active={location.pathname === "/timeline"}>
            <FaHistory /> Timeline
          </BottomNavLink>

          {user.role !== "tech" && (
            <BottomNavLink to="/dashboard" active={location.pathname === "/dashboard"}>
              <FaTachometerAlt /> Dashboard
            </BottomNavLink>
          )}

          <BottomNavLink as="button" onClick={onLogout}>
            <FaSignOutAlt /> Logout
          </BottomNavLink>

        </BottomBar>
      )}
    </>
  );
};

export default Navigation;
